/**
 * DetailView 组件 — 详情页
 * 3D模型展示 + 文字信息面板
 */

import { getUmbrellaById, getPrevUmbrella, getNextUmbrella } from '../app/data.js';
import { navigateToHome, navigateToDetail } from '../app/router.js';
import { createIcon, isTouchDevice } from '../app/utils.js';
import { initScene, disposeScene } from '../three/Scene.js';
import { renderInfoPanel } from './InfoPanel.js';

let currentScene = null;
let currentUmbrella = null;
let isPanelCollapsed = false;

/**
 * 渲染详情页
 */
export function renderDetailView(umbrellaId) {
  const container = document.getElementById('page-detail');
  if (!container) return;

  const data = getUmbrellaById(umbrellaId);
  if (!data) {
    container.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--color-text-secondary);">未找到该展品</div>';
    return;
  }

  currentUmbrella = data;

  const prev = getPrevUmbrella(umbrellaId);
  const next = getNextUmbrella(umbrellaId);

  const hasBg = data.background && data.background.trim() !== '';

  container.innerHTML = `
    <div class="detail-page ${hasBg ? 'has-bg' : ''}" id="detail-page-${data.id}">
      <!-- 背景 -->
      ${hasBg ? `
        <img class="bg-image" src="${data.background}" alt="背景" id="detail-bg">
      ` : '<div class="bg-overlay" style="background: rgba(12,12,12,0.85);"></div>'}

      <!-- 顶部导航 -->
      <nav class="top-nav">
        <a href="#" class="nav-back" id="detail-back">
          ${createIcon('arrowLeft', 20).outerHTML}
          <span>返回</span>
        </a>
        <span class="nav-title">${data.era}</span>
        <span></span>
      </nav>

      <!-- 伞名横幅（与时间轴卡片统一：横版居中、中英结合） -->
      <div class="detail-banner">
        <h2 class="detail-banner-name">${data.name}</h2>
        <p class="detail-banner-name-en">${data.nameEn}</p>
      </div>

      <!-- 中间弹性区：3D 展示 + 时代画作（手机端自适应布局的 flex 伸展区） -->
      <div class="detail-stage">
        <!-- 3D 展示区域 -->
        <div class="detail-viewer" id="detail-viewer"></div>

        <!-- 静态模型渲染图（小红书版：包体限制，部分时期以高清渲染图代替3D） -->
        ${data.modelImg ? `
          <img class="detail-model-img" src="${data.modelImg}" alt="${data.era}·${data.name}">
        ` : ''}

        <!-- 时代画作（长方形画心，置于卡片中间、3D 之后） -->
        ${data.painting ? `
          <figure class="detail-painting">
            <img src="${data.painting}" alt="${data.era}·${data.name}时代画作">
          </figure>
        ` : ''}
      </div>

      <!-- 手势提示（无3D模型的时期不显示） -->
      ${data.noModel ? '' : `
        <div class="detail-gesture-hint breathe" id="gesture-hint">
          <span>单指旋转 · 双指缩放 · 双指拖动平移</span>
        </div>
      `}

      <!-- 信息面板 -->
      <div class="detail-panel" id="detail-panel">
        <div class="detail-panel-handle" id="panel-handle"></div>
        ${renderInfoPanel(data)}
      </div>

      <!-- 固定底栏：上一时期 | 藏品/史料/原型 | 下一时期 -->
      <div class="detail-nav">
        <button class="detail-nav-btn ${!prev ? 'disabled' : ''}" id="btn-prev" data-id="${prev ? prev.id : ''}" title="${prev ? '上一时期：' + prev.name : '无'}" aria-label="上一时期">
          ${createIcon('arrowLeft', 16).outerHTML}
        </button>
        <div class="detail-tags" id="detail-tags">
          ${data.museumItem ? '<button class="detail-tag-btn" data-tag="museum">藏品</button>' : ''}
          <button class="detail-tag-btn" data-tag="sources">史料</button>
          ${(Array.isArray(data.prototypes) && data.prototypes.length > 0) ? '<button class="detail-tag-btn" data-tag="proto">原型</button>' : ''}
        </div>
        <button class="detail-nav-btn ${!next ? 'disabled' : ''}" id="btn-next" data-id="${next ? next.id : ''}" title="${next ? '下一时期：' + next.name : '无'}" aria-label="下一时期">
          ${createIcon('arrowRight', 16).outerHTML}
        </button>
      </div>

      <!-- 底部弹窗（藏品/史料/原型） -->
      <div class="sheet-overlay" id="sheet-overlay"></div>
      <div class="detail-sheet" id="detail-sheet">
        <div class="sheet-handle" id="sheet-handle"></div>
        <div class="sheet-title" id="sheet-title"></div>
        <div class="sheet-body" id="sheet-body"></div>
      </div>
    </div>
  `;

  // 初始化3D场景
  requestAnimationFrame(() => {
    const viewer = document.getElementById('detail-viewer');
    if (viewer) {
      currentScene = initScene(viewer, data);
    }
  });

  bindDetailEvents(data);
}

/**
 * 绑定详情页事件
 */
function bindDetailEvents(data) {
  // 返回按钮
  const backBtn = document.getElementById('detail-back');
  if (backBtn) {
    backBtn.addEventListener('click', (e) => {
      e.preventDefault();
      navigateToHome();
    });
  }

  // 面板折叠/展开
  const panel = document.getElementById('detail-panel');
  const handle = document.getElementById('panel-handle');

  if (handle && panel) {
    handle.addEventListener('click', () => {
      isPanelCollapsed = !isPanelCollapsed;
      panel.classList.toggle('collapsed', isPanelCollapsed);
    });
  }

  // 面板手势滑动
  if (panel && isTouchDevice()) {
    let startY = 0;
    let startPanelY = 0;

    panel.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
      startPanelY = panel.getBoundingClientRect().top;
    }, { passive: true });

    panel.addEventListener('touchmove', (e) => {
      const deltaY = e.touches[0].clientY - startY;
      if (Math.abs(deltaY) > 30) {
        if (deltaY < 0 && !isPanelCollapsed) {
          // 向上滑 — 不做特殊处理（已是展开状态）
        } else if (deltaY > 0 && !isPanelCollapsed && panel.scrollTop === 0) {
          // 向下滑且内容在顶部 — 收起
          isPanelCollapsed = true;
          panel.classList.add('collapsed');
        }
      }
    }, { passive: true });
  }

  // 上一把/下一把
  const prevBtn = document.getElementById('btn-prev');
  const nextBtn = document.getElementById('btn-next');

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      const id = prevBtn.dataset.id;
      if (id) {
        cleanupCurrentScene();
        navigateToDetail(id);
      }
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const id = nextBtn.dataset.id;
      if (id) {
        cleanupCurrentScene();
        navigateToDetail(id);
      }
    });
  }

  // Tab 切换
  const tabGroup = document.getElementById('info-tabs');
  if (tabGroup) {
    tabGroup.addEventListener('click', (e) => {
      const tab = e.target.closest('.tab');
      if (!tab) return;

      const tabName = tab.dataset.tab;
      if (!tabName) return;

      // 更新 tab 状态
      tabGroup.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // 更新内容
      const contents = document.querySelectorAll('.tab-content');
      contents.forEach(c => {
        c.style.display = c.dataset.tab === tabName ? 'block' : 'none';
      });
    });
  }

  // 底部标签（藏品/史料/原型）— 底部弹窗
  const tagsBar = document.getElementById('detail-tags');
  const overlay = document.getElementById('sheet-overlay');
  const sheet = document.getElementById('detail-sheet');
  const sheetHandle = document.getElementById('sheet-handle');

  if (tagsBar && sheet && overlay) {
    tagsBar.addEventListener('click', (e) => {
      const btn = e.target.closest('.detail-tag-btn');
      if (!btn) return;
      openSheet(btn.dataset.tag, data);
    });

    overlay.addEventListener('click', closeSheet);
    if (sheetHandle) {
      sheetHandle.addEventListener('click', closeSheet);
    }
  }

  // 3秒后隐藏手势提示
  setTimeout(() => {
    const hint = document.getElementById('gesture-hint');
    if (hint) {
      hint.style.transition = 'opacity 1s ease';
      hint.style.opacity = '0';
      setTimeout(() => hint.remove(), 1000);
    }
  }, 4000);
}

/**
 * 打开底部弹窗（藏品/史料/原型）
 */
function openSheet(tag, data) {
  const overlay = document.getElementById('sheet-overlay');
  const sheet = document.getElementById('detail-sheet');
  const title = document.getElementById('sheet-title');
  const body = document.getElementById('sheet-body');
  if (!overlay || !sheet || !title || !body) return;

  if (tag === 'museum') {
    title.textContent = '博物馆藏品';
    body.innerHTML = `<p class="sheet-text">${data.museumItem}</p>`;
  } else if (tag === 'sources') {
    title.textContent = '史料与出处';
    body.innerHTML = `
      <ul class="sheet-sources">
        ${(data.sources || []).map(s => `<li>${s}</li>`).join('')}
      </ul>
    `;
  } else if (tag === 'proto') {
    title.textContent = '原型参考';
    body.innerHTML = (data.prototypes || []).map(p => `
      <figure class="sheet-figure">
        <img src="${p.src}" alt="${p.caption}" loading="lazy">
        <figcaption>${p.caption}</figcaption>
      </figure>
    `).join('');
  } else {
    return;
  }

  // 同步标签激活态
  document.querySelectorAll('.detail-tag-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tag === tag);
  });

  overlay.classList.add('visible');
  sheet.classList.add('open');
}

/**
 * 关闭底部弹窗
 */
function closeSheet() {
  const overlay = document.getElementById('sheet-overlay');
  const sheet = document.getElementById('detail-sheet');
  if (overlay) overlay.classList.remove('visible');
  if (sheet) sheet.classList.remove('open');
  document.querySelectorAll('.detail-tag-btn').forEach(b => b.classList.remove('active'));
}

/**
 * 清理当前3D场景
 */
function cleanupCurrentScene() {
  if (currentScene) {
    disposeScene(currentScene);
    currentScene = null;
  }
}
